import { Team, TeamMember } from '../types/domain';

const MY_TEAM_MEMBERSHIP_CACHE_VERSION = 'v1';
const STORAGE_KEY_PREFIX = 'cp.myTeamMembership';

export type CachedMyTeamMembership = {
  userId: string;
  team: Team | null;
  membership: TeamMember | null;
  /** 멤버십 행 시그니처 — 같으면 서버 재조회 생략 */
  membershipVersion?: string;
  savedAt: number;
};

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${MY_TEAM_MEMBERSHIP_CACHE_VERSION}.${userId}`;
}

/** Supabase auth 세션(localStorage)에서 user id만 동기적으로 읽는다(캐시 키용). */
export function readSupabaseSessionUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes('-auth-token')) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as {
        user?: { id?: string };
        currentSession?: { user?: { id?: string } };
      };
      const userId = parsed.user?.id ?? parsed.currentSession?.user?.id ?? '';
      if (userId.trim()) return userId.trim();
    }
  } catch {
    // ignore parse failure
  }
  return null;
}

export function loadMyTeamMembershipCache(userId: string): CachedMyTeamMembership | null {
  const uid = userId.trim();
  if (!uid || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedMyTeamMembership;
    if (!parsed || parsed.userId !== uid || !('membership' in parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveMyTeamMembershipCache(payload: CachedMyTeamMembership): void {
  const uid = payload.userId.trim();
  if (!uid || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    // ignore quota / private mode
  }
}

export function clearMyTeamMembershipCache(userId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId?.trim()) {
      window.localStorage.removeItem(storageKey(userId.trim()));
      return;
    }
    const prefix = `${STORAGE_KEY_PREFIX}.${MY_TEAM_MEMBERSHIP_CACHE_VERSION}.`;
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function resolveTeamIdForLeaderImport(
  workspaceId: string,
  membership: { team: Team | null; membership: Pick<TeamMember, 'status'> | null } | null
): string | null {
  const ws = workspaceId.trim();
  if (!membership?.team || !membership.membership || membership.membership.status !== 'approved') {
    return null;
  }
  return membership.team.workspace_id !== ws ? membership.team.id : null;
}

/** LS에 캐시가 있으면 팀장 가져오기 teamId, 없으면 undefined */
export function loadTeamIdForLeaderImportFromCache(
  workspaceId: string,
  userId?: string | null
): string | null | undefined {
  const uid = (userId ?? readSupabaseSessionUserId())?.trim();
  if (!uid || !workspaceId.trim()) return undefined;
  const cached = loadMyTeamMembershipCache(uid);
  if (!cached) return undefined;
  return resolveTeamIdForLeaderImport(workspaceId, cached);
}

/** LS에 팀 정보가 있으면 팀장 워크스페이스 id. 없으면 undefined(네트워크 필요). */
export function loadTeamLeaderWorkspaceIdForLearningShareFromCache(
  currentWorkspaceId: string | null | undefined
): string | null | undefined {
  const ws = (currentWorkspaceId ?? '').trim();
  if (!ws) return null;
  const uid = readSupabaseSessionUserId();
  if (!uid) return undefined;
  const cached = loadMyTeamMembershipCache(uid);
  if (!cached) return undefined;
  if (!cached.membership || cached.membership.status !== 'approved') return null;
  const leaderWs = cached.team?.workspace_id?.trim() ?? '';
  if (!leaderWs) return null;
  if (leaderWs === ws) return null;
  return leaderWs;
}
